import { CubeState } from './cube-state.js';
import { CubeState4x4 } from './cube-state-4x4.js';
import { deserializeOrientation4x4, serializeOrientation4x4 } from './orientation-4x4.js';
import { THEME_KEY } from './theme.js';

export const STORAGE_PREFIX = 'cube-path:';
export const STORAGE_KEY_2X2 = 'cube-path:entry:2x2:v1';
export const STORAGE_KEY = 'cube-path:entry:3x3:v1';
export const STORAGE_KEY_4X4 = 'cube-path:entry:4x4:v1';
export const ORIENTATION_KEY_4X4 = 'cube-path:orientation:4x4:v1';
export const LAST_ORDER_KEY = 'cube-path:last-order:v1';
export const PLAYBACK_SPEED_KEY = 'cube-path:playback-speed:v1';
export const INPUT_PERSISTENCE_KEY = 'cube-path:input-persistence:v1';
export const TIMER_HISTORY_KEY = 'cube-path:timer-history:v1';
export const TIMER_SAVE_HISTORY_KEY = 'cube-path:timer-save-history:v1';
export const DEFAULT_SCRAMBLE_LENGTH_KEY = 'cube-path:scramble-default-length:v1';
export const PROJECT_CACHE_PREFIXES = Object.freeze(['cube-path-', 'cube-path:']);
export const PROJECT_INDEXED_DB_NAMES = Object.freeze(['cube-path', 'cube-path-db']);
export const LEGACY_STORAGE_KEYS = Object.freeze([
  'cube-state',
  'cube-state-3x3',
  'cube-state-4x4',
  'cube-orientation-4x4',
  'cube-playback-state',
  'cube-solution',
]);

export const PROJECT_LOCAL_STORAGE_KEYS = Object.freeze([
  STORAGE_KEY,
  STORAGE_KEY_2X2,
  STORAGE_KEY_4X4,
  ORIENTATION_KEY_4X4,
  LAST_ORDER_KEY,
  PLAYBACK_SPEED_KEY,
  INPUT_PERSISTENCE_KEY,
  TIMER_HISTORY_KEY,
  TIMER_SAVE_HISTORY_KEY,
  DEFAULT_SCRAMBLE_LENGTH_KEY,
  THEME_KEY,
  ...LEGACY_STORAGE_KEYS,
]);

export function saveCubeState(state, storage = window.localStorage) {
  storage.setItem(STORAGE_KEY, state.serialize());
}

export function saveCubeState2x2(state, storage = window.localStorage) {
  storage.setItem(STORAGE_KEY_2X2, state.serialize());
}

export function loadCubeState2x2(storage = window.localStorage) {
  const serialized = storage.getItem(STORAGE_KEY_2X2);
  if (!serialized) return null;
  try {
    return CubeState.deserialize(serialized);
  } catch {
    storage.removeItem(STORAGE_KEY_2X2);
    return null;
  }
}

export function loadCubeState(storage = window.localStorage) {
  const serialized = storage.getItem(STORAGE_KEY);
  if (!serialized) return null;
  try {
    return CubeState.deserialize(serialized);
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function clearCubeState(storage = window.localStorage) {
  storage.removeItem(STORAGE_KEY);
}

export function clearCubeState2x2(storage = window.localStorage) {
  storage.removeItem(STORAGE_KEY_2X2);
}

export function saveCubeState4x4(state, storage = window.localStorage) {
  storage.setItem(STORAGE_KEY_4X4, state.serialize());
}

export function loadCubeState4x4(orientation, storage = window.localStorage) {
  const serialized = storage.getItem(STORAGE_KEY_4X4);
  if (!serialized) return null;
  try {
    return CubeState4x4.deserialize(serialized, orientation);
  } catch {
    storage.removeItem(STORAGE_KEY_4X4);
    return null;
  }
}

export function clearCubeState4x4(storage = window.localStorage) {
  storage.removeItem(STORAGE_KEY_4X4);
}

export function saveOrientation4x4(orientation, storage = window.localStorage) {
  storage.setItem(ORIENTATION_KEY_4X4, serializeOrientation4x4(orientation));
}

export function loadOrientation4x4(storage = window.localStorage) {
  const serialized = storage.getItem(ORIENTATION_KEY_4X4);
  if (!serialized) return null;
  try {
    return deserializeOrientation4x4(serialized);
  } catch {
    storage.removeItem(ORIENTATION_KEY_4X4);
    return null;
  }
}

export function clearOrientation4x4(storage = window.localStorage) {
  storage.removeItem(ORIENTATION_KEY_4X4);
}

export function saveLastOrder(order, storage = window.localStorage) {
  if (![2, 3, 4].includes(order)) throw new RangeError('最近阶数只能是 2、3 或 4');
  storage.setItem(LAST_ORDER_KEY, JSON.stringify({ version: 1, order }));
}

export function loadLastOrder(storage = window.localStorage) {
  try {
    const data = JSON.parse(storage.getItem(LAST_ORDER_KEY));
    return data?.version === 1 && [2, 3, 4].includes(data.order) ? data.order : null;
  } catch {
    storage.removeItem(LAST_ORDER_KEY);
    return null;
  }
}

export function savePlaybackSpeed(speed, storage = window.localStorage) {
  const allowed = [0.1, 0.25, 0.5, 1, 1.5, 2];
  if (!allowed.includes(Number(speed))) throw new RangeError('不支持的播放速度');
  storage.setItem(PLAYBACK_SPEED_KEY, JSON.stringify({ version: 1, speed: Number(speed) }));
}

export function loadPlaybackSpeed(storage = window.localStorage) {
  try {
    const data = JSON.parse(storage.getItem(PLAYBACK_SPEED_KEY));
    return data?.version === 1 && [0.1, 0.25, 0.5, 1, 1.5, 2].includes(data.speed) ? data.speed : 1;
  } catch {
    storage.removeItem(PLAYBACK_SPEED_KEY);
    return 1;
  }
}

export function loadInputPersistencePreference(storage = window.localStorage) {
  try {
    return storage.getItem(INPUT_PERSISTENCE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function saveInputPersistencePreference(enabled, storage = window.localStorage) {
  storage.setItem(INPUT_PERSISTENCE_KEY, String(Boolean(enabled)));
  return Boolean(enabled);
}

export function clearSavedCubeInputs(storage = window.localStorage) {
  storage.removeItem(STORAGE_KEY_2X2);
  storage.removeItem(STORAGE_KEY);
  storage.removeItem(STORAGE_KEY_4X4);
  storage.removeItem(ORIENTATION_KEY_4X4);
}

export function loadPersistedCubeInput(order, {
  enabled = false,
  orientation = null,
  storage = window.localStorage,
} = {}) {
  if (!enabled) return null;
  if (order === 2) return loadCubeState2x2(storage);
  if (order === 3) return loadCubeState(storage);
  if (order === 4) return orientation ? loadCubeState4x4(orientation, storage) : null;
  throw new RangeError('魔方阶数只能是 2、3 或 4');
}

export function savePersistedCubeInput(order, state, {
  enabled = false,
  orientation = null,
  storage = window.localStorage,
} = {}) {
  if (!enabled) return false;
  if (order === 2) saveCubeState2x2(state, storage);
  else if (order === 3) saveCubeState(state, storage);
  else if (order === 4) {
    saveCubeState4x4(state, storage);
    if (orientation) saveOrientation4x4(orientation, storage);
  } else throw new RangeError('魔方阶数只能是 2、3 或 4');
  return true;
}

function deleteIndexedDb(name, indexedDB) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error(`无法删除数据库 ${name}`));
    request.onblocked = () => reject(new Error(`数据库 ${name} 正在被占用，请关闭其他本网站页面后重试`));
  });
}

export async function deleteAllProjectLocalData({
  localStorage = window.localStorage,
  sessionStorage = window.sessionStorage,
  cacheStorage = globalThis.caches,
  indexedDB = globalThis.indexedDB,
} = {}) {
  try {
    PROJECT_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) sessionStorage.removeItem(key);
    }
    const deletedCaches = [];
    if (cacheStorage) {
      const cacheNames = await cacheStorage.keys();
      for (const name of cacheNames) {
        if (PROJECT_CACHE_PREFIXES.some((prefix) => name.startsWith(prefix))) {
          const deleted = await cacheStorage.delete(name);
          if (!deleted) throw new Error(`无法删除缓存 ${name}`);
          deletedCaches.push(name);
        }
      }
    }
    if (indexedDB) {
      for (const name of PROJECT_INDEXED_DB_NAMES) await deleteIndexedDb(name, indexedDB);
    }
    return {
      localStorageKeys: [...PROJECT_LOCAL_STORAGE_KEYS],
      sessionStoragePrefix: STORAGE_PREFIX,
      indexedDbNames: [...PROJECT_INDEXED_DB_NAMES],
      cacheNames: deletedCaches,
    };
  } catch (cause) {
    throw new Error(`删除本地数据失败：${cause?.message ?? String(cause)}`, { cause });
  }
}
